import { HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '@features/auth/services/auth.service';
import { apiErrorMessage } from '@shared/utils/api-error';

// ── Token-refresh state (module-level, shared across all interceptor calls) ──
let isRefreshingToken = false;
// Emits null while refresh is in-flight; emits the new token when done.
// All concurrent 401 requests wait here and retry once a token is emitted.
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export function errorInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn) {
  const authService = inject(AuthService);
  const router = inject(Router);

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      // Skip error handling for external requests (e.g. GCP signed URL uploads)
      if (isExternalRequest(request)) {
        return throwError(() => error);
      }

      console.error(`[Error Interceptor] HTTP ${error.status}:`, error.url);

      switch (error.status) {
        case 401:
          return handle401Error(request, next, authService, router, error);
        case 403:
          // Propagate to the component — don't force-navigate away
          console.error('[Error Interceptor] 403 Forbidden:', error.url);
          return throwError(() => error);
        case 404:
          console.error('[Error Interceptor] 404 Not Found:', error.url);
          return throwError(() => error);
        case 500:
          console.error('[Error Interceptor] 500 Server Error:', error.url);
          return throwError(() => new Error('An internal server error occurred. Please try again later.'));
        default:
          return throwError(() => error);
      }
    })
  );
}

// ✅ Handle 401 — with proper BehaviorSubject queuing for concurrent requests
function handle401Error(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router,
  error: HttpErrorResponse
) {
  // Case 1: Public endpoints (registration form, uploads, etc.)
  // These don't require auth — a 401 just means the BE rejected the payload.
  // Propagate the error to the component; never logout.
  if (isPublicEndpoint(request)) {
    return throwError(() => new Error(apiErrorMessage(error, 'Request failed. Please check your input and try again.')));
  }

  // Case 2: Auth/login endpoints where 401 = bad credentials → logout immediately
  if (isLoginEndpoint(request)) {
    isRefreshingToken = false;
    authService.logout();
    router.navigate(['/auth/login']);
    return throwError(() => new Error('Authentication failed'));
  }

  // Case 3: Protected endpoints — try token refresh
  if (!authService.getRefreshToken()) {
    isRefreshingToken = false;
    authService.logout();
    router.navigate(['/auth/login']);
    return throwError(() => new Error('No refresh token available'));
  }

  if (isRefreshingToken) {
    // ── QUEUE: another refresh is already in-flight — wait for it to emit a token
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => {
        const retried = request.clone({
          headers: request.headers.set('Authorization', `Bearer ${token}`)
        });
        return next(retried);
      })
    );
  }

  // ── Kick off a single token refresh ──
  isRefreshingToken = true;
  refreshTokenSubject.next(null); // tell all queued requests "refresh is in progress"

  return authService.refreshAccessToken().pipe(
    switchMap((newToken: string) => {
      isRefreshingToken = false;
      refreshTokenSubject.next(newToken); // unblock all queued requests

      const retried = request.clone({
        headers: request.headers.set('Authorization', `Bearer ${newToken}`)
      });
      return next(retried);
    }),
    catchError((refreshError) => {
      isRefreshingToken = false;
      refreshTokenSubject.next(null);

      console.error('[401 Handler] Token refresh failed — logging out:', refreshError);
      authService.logout();

      setTimeout(() => {
        if (!router.url.includes('/auth/login')) {
          router.navigate(['/auth/login']);
        }
      }, 100);

      return throwError(() => new Error('Session expired. Please log in again.'));
    })
  );
}

// ── Auth endpoint helpers ────────────────────────────────────────────────────

/** Login/OTP/refresh endpoints — a 401 here means bad credentials → logout immediately */
function isLoginEndpoint(request: HttpRequest<unknown>): boolean {
  return ['/auth/login', '/auth/otp', '/auth/refresh-token']
    .some(e => request.url.includes(e));
}

/** Public API endpoints — no auth required, 401 should NOT trigger logout */
function isPublicEndpoint(request: HttpRequest<unknown>): boolean {
  const url = request.url;
  const method = request.method;

  const alwaysPublic = [
    '/business-registration',
    '/individuals-registration',
    '/individuals-registration-v2',
    // BE-111: coordinator email reject/approve pages — token-authed, no JWT.
    '/permission-requests/actions',
    '/api/v3/banks',
    '/public'
  ];
  if (alwaysPublic.some(e => url.includes(e))) return true;

  // POST /registration-requests = public form submission
  if (url.includes('/registration-requests') && method === 'POST') return true;

  // Upload calls used during public registration (no auth needed)
  if (url.includes('/upload') && (method === 'POST' || method === 'PUT')) return true;

  return false;
}

/** isAuthRequest = either a login endpoint or a public endpoint (used by auth interceptor) */
function isAuthRequest(request: HttpRequest<unknown>): boolean {
  return isLoginEndpoint(request) || isPublicEndpoint(request);
}


// ✅ Check if request goes to an external host (GCP, S3, Azure) — bypass auth logic entirely
function isExternalRequest(request: HttpRequest<unknown>): boolean {
  const url = request.url;
  if (!url.startsWith('http')) return false;
  const externalHosts = [
    'googleapis.com',
    'storage.cloud.google.com',
    'amazonaws.com',
    'blob.core.windows.net',
    'nominatim.openstreetmap.org',
    'maps.googleapis.com',
  ];
  return externalHosts.some(host => url.includes(host));
}
